import { z } from 'zod'
import { calendarDate } from './dates'

/**
 * Time off: whole days, from the first to the last day inclusive, as local dates in the user's
 * time zone, with an optional label ("Vacation"). Periods may overlap: a day is off when any
 * period covers it. Time off is not working time, wherever hugo counts working time (insights
 * durations, the planner).
 */
export const TIME_OFF_LABEL_MAX = 60
/** The most days one period may cover, first and last day included. */
export const TIME_OFF_DAYS_MAX = 366

/** The default of how many days ahead hugo announces time off (the user's rule `awaySoonDays`). */
export const AWAY_SOON_DAYS = 14

/**
 * Whether the user is away: now, until the last day of the absence (time-off periods with only
 * non-working days between them count as one), or soon, from its first day (within
 * AWAY_SOON_DAYS); null otherwise.
 */
export const awayStatusSchema = z
  .discriminatedUnion('state', [
    z.object({ state: z.literal('away'), until: calendarDate }),
    z.object({ state: z.literal('soon'), from: calendarDate }),
  ])
  .nullable()
export type AwayStatus = z.output<typeof awayStatusSchema>

export const timeOffSchema = z.object({
  id: z.uuid(),
  startsOn: calendarDate,
  endsOn: calendarDate,
  label: z.string().nullable(),
  /** Working days the period takes off: weekends, days without working hours and holidays excluded. */
  workingDays: z.number().int().min(0),
  createdAt: z.iso.datetime(),
})
export type TimeOff = z.output<typeof timeOffSchema>

const DAY_MS = 86_400_000

/**
 * Days from the first to the last day, both included (less than 1 when it ends before it
 * starts); null when either is not a real date (that is reported on its own field).
 */
function daysCovered(startsOn: string, endsOn: string): number | null {
  if (!calendarDate.safeParse(startsOn).success || !calendarDate.safeParse(endsOn).success) {
    return null
  }
  // Date-only ISO strings parse as UTC midnight: whole days apart, no DST in between.
  return Math.round((Date.parse(endsOn) - Date.parse(startsOn)) / DAY_MS) + 1
}

/** A label is trimmed and at most TIME_OFF_LABEL_MAX characters; a blank one means none. */
const timeOffLabel = z
  .string()
  .trim()
  .max(TIME_OFF_LABEL_MAX)
  .transform((label) => label || null)

export const timeOffInputSchema = z
  .object({
    startsOn: calendarDate,
    endsOn: calendarDate,
    label: timeOffLabel.nullable().default(null),
  })
  .refine((p) => (daysCovered(p.startsOn, p.endsOn) ?? 1) >= 1, {
    path: ['endsOn'],
    message: 'Time off cannot end before its first day',
  })
  .refine((p) => (daysCovered(p.startsOn, p.endsOn) ?? 1) <= TIME_OFF_DAYS_MAX, {
    path: ['endsOn'],
    message: `Time off covers at most ${TIME_OFF_DAYS_MAX} days`,
  })
export type TimeOffInput = z.input<typeof timeOffInputSchema>

/** Keys of time-off suggestions (`ooo:<source record>:<first day>:<last day>`) are bounded. */
export const TIME_OFF_SUGGESTION_KEY_MAX = 600

/**
 * Whole days hugo suggests adding as time off, because a connected calendar says the user is out
 * of office then (a suggestion until the user adds or dismisses it). Accepting it is adding the
 * period with `time_off.add`.
 */
export const timeOffSuggestionSchema = z.object({
  key: z.string().min(1).max(TIME_OFF_SUGGESTION_KEY_MAX),
  startsOn: calendarDate,
  endsOn: calendarDate,
  /** The calendar event's title (at most TIME_OFF_LABEL_MAX characters); null without one. */
  label: z.string().nullable(),
  /** Working days it would take off. */
  workingDays: z.number().int().min(0),
  /** The event in the calendar. */
  url: z.string().nullable(),
})
export type TimeOffSuggestion = z.output<typeof timeOffSuggestionSchema>
